import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { keyframes } from '@mui/system';

// Define process steps
interface ProcessStep {
  id: number;
  label: string;
  description?: string;
  durationMs: number;
}

interface FlowChartLoaderProps {
  onComplete?: () => void;
  horizontalLayout?: boolean;
  showLabels?: boolean;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
}

// Animation keyframes
const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

const scan = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  50% { opacity: 0.8; }
  100% { transform: translateY(100%); opacity: 0; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(88, 255, 242, 0.5); }
  50% { box-shadow: 0 0 20px rgba(88, 255, 242, 0.8); }
  100% { box-shadow: 0 0 5px rgba(88, 255, 242, 0.5); }
`;

const celebrate = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

const FlowChartLoader: React.FC<FlowChartLoaderProps> = ({
  onComplete,
  horizontalLayout = true,
  showLabels = true,
  primaryColor,
  secondaryColor,
  backgroundColor = '#1A1E2E' // Dark blue/gray background
}) => {
  const theme = useTheme();
  
  // Use theme colors if no custom colors provided
  const primaryColorFinal = primaryColor || theme.palette.primary.main;
  const secondaryColorFinal = secondaryColor || theme.palette.secondary.main;
  
  // Define the process steps
  const processSteps: ProcessStep[] = [
    { id: 0, label: 'Data Import', description: 'Importing your data...', durationMs: 2000 },
    { id: 1, label: 'Consolidating Suppliers', description: 'Analyzing supplier information...', durationMs: 2500 },
    { id: 2, label: 'Building Supplier Profiles', description: 'Creating comprehensive profiles...', durationMs: 3000 },
    { id: 3, label: 'Searching Items', description: 'Locating items in database...', durationMs: 2000 },
    { id: 4, label: 'Mapping Items', description: 'Establishing item relationships...', durationMs: 2500 },
    { id: 5, label: 'Validating Item Mappings', description: 'Ensuring accurate mapping...', durationMs: 2000 },
    { id: 6, label: 'Evaluating Spend', description: 'Analyzing spending patterns...', durationMs: 2500 }
  ];
  
  const [currentStep, setCurrentStep] = useState<number>(-1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isComplete, setIsComplete] = useState<boolean>(false);

  useEffect(() => {
    // Start the process
    let totalDelay = 0;
    
    // For each step, set a timeout based on the cumulative duration
    processSteps.forEach((step, index) => {
      // Start the step after the previous ones have completed
      setTimeout(() => {
        setCurrentStep(step.id);
      }, totalDelay);
      
      // Add this step's duration to the total
      totalDelay += step.durationMs;
      
      // Mark the step as completed after its duration
      setTimeout(() => {
        setCompletedSteps(prev => [...prev, step.id]);
        
        // If this is the last step, mark the process as complete
        if (index === processSteps.length - 1) {
          setIsComplete(true);
          if (onComplete) {
            setTimeout(() => {
              onComplete();
            }, 1500); // Delay after completion animation
          }
        }
      }, totalDelay);
    });
  }, []);

  // Dynamic glow keyframe with the correct primary color
  const dynamicGlow = keyframes`
    0% { box-shadow: 0 0 5px ${primaryColorFinal}80; }
    50% { box-shadow: 0 0 20px ${primaryColorFinal}; }
    100% { box-shadow: 0 0 5px ${primaryColorFinal}80; }
  `;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor,
        zIndex: 9999,
        overflow: 'hidden',
        padding: 4,
      }}
    >
      {/* Optional subtle background pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: 'linear-gradient(to bottom, #ffffff11 1px, transparent 1px), linear-gradient(to right, #ffffff11 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }}
      />
      
      {/* Main title */}
      <Typography
        variant="h4"
        sx={{
          color: '#fff',
          fontFamily: '"Orbitron", "Roboto", sans-serif',
          marginBottom: 5,
          textAlign: 'center',
          fontWeight: 500,
          letterSpacing: 2,
          textShadow: `0 0 10px ${primaryColorFinal}88`,
        }}
      >
        PROCESSING
      </Typography>
      
      {/* Flow chart container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: horizontalLayout ? 'row' : 'column',
          justifyContent: 'center',
          alignItems: 'center',
          maxWidth: '90%',
          gap: horizontalLayout ? 2 : 3,
          position: 'relative',
          animation: `${float} 6s ease-in-out infinite`,
          flexWrap: horizontalLayout ? 'wrap' : 'nowrap',
          padding: 3,
        }}
      >
        {processSteps.map((step, index) => {
          const isActive = currentStep === step.id;
          const isCompleted = completedSteps.includes(step.id);
          
          return (
            <React.Fragment key={step.id}>
              {/* Step node */}
              <Box
                sx={{
                  position: 'relative',
                  width: 70,
                  height: 70,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px solid ${
                    isCompleted 
                      ? primaryColorFinal 
                      : isActive 
                        ? secondaryColorFinal 
                        : 'rgba(255,255,255,0.2)'
                  }`,
                  boxShadow: isActive 
                    ? `0 0 15px ${secondaryColorFinal}` 
                    : isCompleted 
                      ? `0 0 15px ${primaryColorFinal}` 
                      : 'none',
                  animation: isActive 
                    ? `${pulse} 1.5s infinite ease-in-out, ${dynamicGlow} 2s infinite ease-in-out` 
                    : isCompleted && isComplete && index === processSteps.length - 1
                      ? `${celebrate} 1s ease-in-out` 
                      : 'none',
                  zIndex: 2,
                  transition: 'all 0.3s ease-in-out',
                  transform: isActive ? 'scale(1.1)' : 'scale(1)',
                }}
              >
                {isCompleted ? (
                  <CheckCircleIcon 
                    sx={{ 
                      color: primaryColorFinal,
                      fontSize: 32,
                    }} 
                  />
                ) : (
                  <Typography 
                    variant="body1"
                    sx={{
                      color: isActive ? secondaryColorFinal : 'rgba(255,255,255,0.5)',
                      fontWeight: 'bold',
                      fontSize: '1.2rem',
                    }}
                  >
                    {step.id + 1}
                  </Typography>
                )}
                
                {/* Digital scan effect when becoming active */}
                {isActive && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(to bottom, transparent, ${secondaryColorFinal}, transparent)`,
                      animation: `${scan} 1.5s ease-in-out infinite`,
                      opacity: 0.3,
                      pointerEvents: 'none',
                      borderRadius: '50%',
                      zIndex: 3,
                    }}
                  />
                )}
                
                {/* Step label */}
                {showLabels && (
                  <Typography
                    variant="body2"
                    sx={{
                      position: 'absolute',
                      top: horizontalLayout ? '100%' : '50%',
                      left: horizontalLayout ? '50%' : '100%',
                      transform: horizontalLayout 
                        ? 'translateX(-50%)' 
                        : 'translateY(-50%)',
                      marginTop: horizontalLayout ? 1 : 0,
                      marginLeft: horizontalLayout ? 0 : 2,
                      color: isActive 
                        ? secondaryColorFinal 
                        : isCompleted 
                          ? primaryColorFinal 
                          : 'rgba(255,255,255,0.5)',
                      fontFamily: '"Orbitron", "Roboto", sans-serif',
                      fontSize: '0.8rem',
                      textAlign: 'center',
                      width: horizontalLayout ? 100 : 'auto',
                      whiteSpace: horizontalLayout ? 'normal' : 'nowrap',
                      textShadow: isActive || isCompleted 
                        ? `0 0 5px ${isActive ? secondaryColorFinal : primaryColorFinal}`
                        : 'none',
                    }}
                  >
                    {step.label}
                  </Typography>
                )}
              </Box>
              
              {/* Connection line between steps */}
              {index < processSteps.length - 1 && (
                <Box
                  sx={{
                    position: 'relative',
                    width: horizontalLayout ? 50 : 2,
                    height: horizontalLayout ? 2 : 50,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    zIndex: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: isCompleted ? '100%' : currentStep === step.id ? '50%' : '0%',
                      height: '100%',
                      background: `linear-gradient(to ${horizontalLayout ? 'right' : 'bottom'}, ${primaryColorFinal}, ${secondaryColorFinal})`,
                      transition: 'width 0.5s ease-out, height 0.5s ease-out',
                    }}
                  />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>
      
      {/* Current step description */}
      <Box
        sx={{
          marginTop: 4,
          height: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {currentStep >= 0 && !isComplete && (
          <Typography
            variant="body1"
            sx={{
              color: '#fff',
              fontFamily: '"Roboto", sans-serif',
              opacity: 0.8,
              textAlign: 'center',
            }}
          >
            {processSteps.find(step => step.id === currentStep)?.description || ''}
          </Typography>
        )}
        
        {isComplete && (
          <Typography
            variant="h6"
            sx={{
              color: primaryColorFinal,
              fontFamily: '"Orbitron", "Roboto", sans-serif',
              textAlign: 'center',
              animation: `${celebrate} 1s ease-in-out`,
              textShadow: `0 0 10px ${primaryColorFinal}`,
              fontWeight: 'bold',
            }}
          >
            PROCESS COMPLETE
          </Typography>
        )}
      </Box>
      
      {/* Small branding logo/text in corner */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          opacity: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#fff',
            fontFamily: '"Orbitron", "Roboto", sans-serif',
            letterSpacing: 1,
          }}
        >
          SPEND ANALYTICS
        </Typography>
      </Box>
    </Box>
  );
};

export default FlowChartLoader; 